import { css } from '@emotion/react';
import { Box, Button, Flex, Text } from '@inflearn/ds-react-next';
import { Trans, useTranslation } from 'next-i18next';

import { INFLEARN_B2B_URL } from '../../../../constants/urls';
import { HERO_BACKGROUND_ROWS } from './subscriptionCoursesHeroBackground';

/*
 * 실시간 집계가 아니라 고정 문구다. 개수만을 위해 검색 API 를 한 번 더 부르지 않으려는 것도 있고,
 * dev 는 강의가 200여 개뿐이라 실제 개수를 노출하면 확인할 때마다 문구가 어긋난다.
 * 구독 지원 강의가 크게 늘면 이 값을 손으로 올린다.
 */
const COURSE_COUNT_LABEL = '4,000개+';

interface SubscriptionCoursesHeroProps {
  /** 파트너(인재개발플랫폼) 경로로 들어온 방문자에게는 B2B 도입 접점을 보이지 않는다 */
  hideBusinessCtas?: boolean;
}

export const SubscriptionCoursesHero = ({
  hideBusinessCtas = false,
}: SubscriptionCoursesHeroProps) => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styleHero}>
      <HeroBackgroundRows />
      <Box css={styleHeroContent}>
        <Text component="p" css={styleEyebrow}>
          {t('subscription_courses.hero.eyebrow', { courseCount: COURSE_COUNT_LABEL })}
        </Text>
        <Box component="h1" css={styleTitle}>
          <Trans i18nKey="subscription_courses.hero.title" components={{ br: <br /> }} />
        </Box>
        {hideBusinessCtas ? null : (
          <Flex gap={8} justify="center" wrap="wrap" css={styleCtaGroup}>
            <Button
              size="md"
              radius="md"
              component="a"
              href={INFLEARN_B2B_URL.SUPPORT()}
              css={stylePrimaryCta}
            >
              {t('subscription_courses.cta.contact')}
            </Button>
            <Button
              size="md"
              radius="md"
              component="a"
              href={INFLEARN_B2B_URL.INTRO()}
              css={styleSecondaryCta}
            >
              {t('subscription_courses.cta.learn_more')}
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

/* 강의가 "많다"는 인상만 주는 장식이라 흐리게 깔고 위쪽은 마스크로 지워 타이틀과 겹치지 않게 한다 */
const HeroBackgroundRows = () => (
  <Box aria-hidden css={styleBackground}>
    {HERO_BACKGROUND_ROWS.map((row, rowIndex) => (
      <Box key={rowIndex} css={styleBackgroundRow}>
        {row.map((src) => (
          <img key={src} src={src} alt="" loading="lazy" css={styleBackgroundThumb} />
        ))}
      </Box>
    ))}
  </Box>
);

const styleHero = css({
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(180deg, #16241b 0%, #0f1a13 100%)',
  padding: 'clamp(50px, 6.4vw, 88px) 0 clamp(74px, 9vw, 112px)',
});

const styleHeroContent = css({
  position: 'relative',
  zIndex: 2,
  maxWidth: 'min(1120px, 100%)',
  margin: '0 auto',
  padding: '0 clamp(16px, 4vw, 32px)',
  textAlign: 'center',
});

const styleEyebrow = css({
  margin: '0 auto clamp(10px, 1.8vw, 16px)',
  color: '#a9cba1',
  fontSize: 'clamp(14px, 3.4vw, 17px)',
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '-0.01em',
  wordBreak: 'keep-all',
});

const styleTitle = css({
  margin: 0,
  color: '#ffffff',
  fontSize: 'clamp(23px, 7.5vw, 46px)',
  fontWeight: 800,
  lineHeight: 1.26,
  letterSpacing: '-0.01em',
  wordBreak: 'keep-all',
});

const styleCtaGroup = css({
  marginTop: 'clamp(20px, 3vw, 32px)',
});

const stylePrimaryCta = css({
  backgroundColor: '#1aa85a',
  '&:hover': { backgroundColor: '#0f9d52' },
});

const styleSecondaryCta = css({
  backgroundColor: 'transparent',
  color: '#ffffff',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
});

const styleBackground = css({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 'clamp(-58px, -5vw, -36px)',
  zIndex: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'clamp(2px, 0.6vw, 7px)',
  alignItems: 'center',
  opacity: 0.13,
  filter: 'blur(3px) saturate(0.85) brightness(0.9)',
  pointerEvents: 'none',
  maskImage: 'linear-gradient(180deg, transparent 0%, #000 42%, #000 100%)',
  WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 42%, #000 100%)',
});

const styleBackgroundRow = css({
  display: 'flex',
  gap: 'clamp(2px, 0.4vw, 5px)',
  justifyContent: 'center',
  alignItems: 'flex-end',
});

const styleBackgroundThumb = css({
  height: 'clamp(120px, 16vw, 196px)',
  width: 'auto',
  maxWidth: '100%',
  borderRadius: 16,
  flex: 'none',
  display: 'block',
});
